import React, { useState, useEffect, useCallback } from "react";
import { useUnifiedService } from "../../../utilities/contexts/UnifiedServiceContext";
import { Button, Card, Row, Col } from "antd";
import useMediaPipeFaceDetection from "../../../utilities/hooks/useMediaPipeFaceDetection";

const UnifiedFaceBlurStep: React.FC = () => {
  const { serviceInterface, bodyMeasurement, resetAll } = useUnifiedService();
  const { goToNextStep } = serviceInterface;
  const { frontImage, sideImage, setFrontImage, setSideImage } = bodyMeasurement;

  const [processing, setProcessing] = useState(false);
  const [processingResult, setProcessingResult] = useState<{
    frontBlurred: boolean;
    sideBlurred: boolean;
  } | null>(null);

  const { blurFaces, isInitialized } = useMediaPipeFaceDetection({
    fallbackBlur: false,
  });

  const processImages = useCallback(async () => {
    if (!frontImage.base64 || !sideImage.base64) return;

    setProcessing(true);

    try {
      const frontResult = await blurFaces(frontImage.base64);
      const frontBlurred = frontResult.success && frontResult.originalFacesCount > 0;

      if (frontBlurred && frontResult.blurredImage) {
        setFrontImage({
          ...frontImage,
          blurredBase64: frontResult.blurredImage,
        });
      }

      const sideResult = await blurFaces(sideImage.base64);
      const sideBlurred = sideResult.success && sideResult.originalFacesCount > 0;

      if (sideBlurred && sideResult.blurredImage) {
        setSideImage({
          ...sideImage,
          blurredBase64: sideResult.blurredImage,
        });
      }

      setProcessingResult({
        frontBlurred,
        sideBlurred,
      });
    } catch (error) {
      setProcessingResult({
        frontBlurred: false,
        sideBlurred: false,
      });
    } finally {
      setProcessing(false);
    }
  }, [frontImage, sideImage, setFrontImage, setSideImage, blurFaces]);

  useEffect(() => {
    if (isInitialized && !processing && !processingResult && frontImage.base64 && sideImage.base64) {
      processImages();
    }
  }, [isInitialized, processing, processingResult, frontImage.base64, sideImage.base64]);

  const handleGoBack = () => {
    resetAll();
  };

  const handleContinue = () => {
    goToNextStep();
  };

  if (!isInitialized || processing) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-8 h-8 mb-4 border-2 border-blue-500 rounded-full border-t-transparent animate-spin"></div>
        <p className="text-gray-600">{!isInitialized ? "Initializing face detection..." : "Processing images..."}</p>
      </div>
    );
  }

  const formatImageSrc = (base64String: string): string => {
    if (!base64String) return "";

    if (base64String.startsWith("data:")) {
      return base64String;
    }

    return `data:image/jpeg;base64,${base64String}`;
  };

  if (processingResult) {
    const frontImageToShow = frontImage.blurredBase64 || frontImage.base64;
    const sideImageToShow = sideImage.blurredBase64 || sideImage.base64;

    return (
      <div className="max-w-4xl p-6 mx-auto">
        <div className="mb-8 text-center">
          <div className="p-4 mb-6">
            <h2 className="mb-4 text-xl font-semibold text-gray-800">Face Detection Processing</h2>
            <p className="mb-4 text-sm leading-relaxed text-gray-600">
              We've analyzed your images using advanced AI face detection technology. Any detected faces have been automatically blurred to protect your privacy before processing continues.
            </p>

            <div className="p-4 bg-white border border-gray-200 rounded-md">
              <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-700">Front Image:</span>
                  <div className="flex items-center space-x-2">
                    {processingResult.frontBlurred ? (
                      <>
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        <span className="font-medium text-green-700">Face Blurred</span>
                      </>
                    ) : (
                      <>
                        <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                        <span className="text-gray-600">No Face Detected</span>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-700">Side Image:</span>
                  <div className="flex items-center space-x-2">
                    {processingResult.sideBlurred ? (
                      <>
                        <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        <span className="font-medium text-green-700">Face Blurred</span>
                      </>
                    ) : (
                      <>
                        <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                        <span className="text-gray-600">No Face Detected</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Row gutter={[16, 16]} className="mb-4">
          <Col xs={24} md={12}>
            <Card title="Front Image" className="text-center">
              <img src={formatImageSrc(frontImageToShow)} alt="Front view" className="w-full max-w-sm mx-auto rounded-lg" style={{ maxHeight: "300px", objectFit: "contain" }} />
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card title="Side Image" className="text-center">
              <img src={formatImageSrc(sideImageToShow)} alt="Side view" className="w-full max-w-sm mx-auto rounded-lg" style={{ maxHeight: "300px", objectFit: "contain" }} />
            </Card>
          </Col>
        </Row>

        <div className="flex flex-col justify-center gap-4 sm:flex-row">
          <Button size="large" onClick={handleGoBack} className="h-auto px-8 py-2">
            Go Back & Change Images
          </Button>
          <Button type="primary" size="large" onClick={handleContinue} className="h-auto px-8 py-2 bg-blue-600 hover:bg-blue-700">
            Continue
          </Button>
        </div>

        <div className="mt-6 text-sm text-center text-gray-500">Face detection processed locally on your device</div>
      </div>
    );
  }

  return null;
};

export default UnifiedFaceBlurStep;
